import { ActionIcon, Box, Button, Combobox, Group, ScrollArea, Text, useCombobox } from '@mantine/core';
import { IconChevronRight, IconCirclePlus, IconSearch, IconStarFilled } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type PickerProject, useProjectPicker } from '@/api/catalog';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { ProjectDot } from './ProjectDot';

export interface ProjectSelection {
  projectId?: string | null;
  taskId?: string | null;
}

interface Props {
  value: ProjectSelection;
  onChange: (value: ProjectSelection, project?: PickerProject) => void;
  required?: boolean;
  /** Darstellung als kompakter Button (Timer-Leiste) oder als Feld */
  variant?: 'button' | 'field';
  label?: string;
  disabled?: boolean;
  onCreateProject?: (name: string) => void;
}

/** Projekt-/Task-Auswahl mit Suche, Gruppierung nach Client und Favoriten (TT-04). */
export function ProjectPicker({ value, onChange, required, variant = 'button', label, disabled, onCreateProject }: Props) {
  const { t } = useTranslation();
  const { workspaceId } = useCurrentWorkspace();
  const { data: projects = [] } = useProjectPicker(workspaceId);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const combobox = useCombobox({
    onDropdownClose: () => {
      setSearch('');
      setExpanded(null);
    },
  });

  const selected = projects.find((p) => p.id === value.projectId);
  const selectedTask = selected?.tasks.find((task) => task.id === value.taskId);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = projects.filter(
      (p) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        (p.clientName ?? '').toLowerCase().includes(q) ||
        p.tasks.some((task) => task.name.toLowerCase().includes(q)),
    );
    const favorites = matches.filter((p) => p.favorite);
    const byClient = new Map<string, PickerProject[]>();
    for (const p of matches) {
      const key = p.clientName ?? '';
      byClient.set(key, [...(byClient.get(key) ?? []), p]);
    }
    return { favorites, byClient: [...byClient.entries()].sort(([a], [b]) => a.localeCompare(b)) };
  }, [projects, search]);

  const select = (project?: PickerProject, taskId?: string) => {
    onChange({ projectId: project?.id ?? null, taskId: taskId ?? null }, project);
    combobox.closeDropdown();
  };

  const renderProject = (p: PickerProject, keyPrefix: string) => {
    const q = search.trim().toLowerCase();
    const showTasks = expanded === `${keyPrefix}${p.id}` || (!!q && p.tasks.some((task) => task.name.toLowerCase().includes(q)));
    return (
      <Box key={`${keyPrefix}${p.id}`}>
        <Combobox.Option value={`${keyPrefix}${p.id}`} onClick={() => select(p)}>
          <Group justify="space-between" wrap="nowrap">
            <ProjectDot name={p.name} color={p.color} />
            {p.tasks.length > 0 && (
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                aria-label={t('picker.showTasks')}
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(showTasks ? null : `${keyPrefix}${p.id}`);
                }}
              >
                <Text size="xs" mr={2}>
                  {p.tasks.length}
                </Text>
                <IconChevronRight size={14} style={{ transform: showTasks ? 'rotate(90deg)' : undefined }} />
              </ActionIcon>
            )}
          </Group>
        </Combobox.Option>
        {showTasks &&
          p.tasks
            .filter((task) => !q || task.name.toLowerCase().includes(q) || p.name.toLowerCase().includes(q))
            .map((task) => (
              <Combobox.Option key={task.id} value={`${keyPrefix}${p.id}:${task.id}`} pl={32} onClick={() => select(p, task.id)}>
                <Text size="sm">{task.name}</Text>
              </Combobox.Option>
            ))}
      </Box>
    );
  };

  const target =
    variant === 'button' ? (
      <Button
        variant="subtle"
        color={selected ? 'gray' : required ? 'red' : 'orange'}
        leftSection={selected ? undefined : <IconCirclePlus size={18} />}
        onClick={() => combobox.toggleDropdown()}
        disabled={disabled}
        px="xs"
        styles={{ label: { maxWidth: 260 } }}
      >
        {selected ? (
          <ProjectDot name={selected.name} color={selected.color} taskName={selectedTask?.name} clientName={selected.clientName} />
        ) : (
          <>
            {label ?? t('picker.project')}
            {required && (
              <Text span c="red" ml={2}>
                *
              </Text>
            )}
          </>
        )}
      </Button>
    ) : (
      <Box>
        {label && (
          <Text size="sm" fw={500} mb={4}>
            {label}
            {required && (
              <Text span c="red" ml={2}>
                *
              </Text>
            )}
          </Text>
        )}
        <Button
          variant="default"
          fullWidth
          justify="space-between"
          onClick={() => combobox.toggleDropdown()}
          disabled={disabled}
          rightSection={<IconChevronRight size={14} />}
        >
          {selected ? (
            <ProjectDot name={selected.name} color={selected.color} taskName={selectedTask?.name} />
          ) : (
            <Text size="sm" c="dimmed">
              {t('picker.noProject')}
            </Text>
          )}
        </Button>
      </Box>
    );

  return (
    <Combobox store={combobox} width={340} position="bottom-start" withinPortal>
      <Combobox.Target>{target}</Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          placeholder={t('picker.search')}
          leftSection={<IconSearch size={14} />}
        />
        <Combobox.Options>
          <ScrollArea.Autosize mah={320} type="scroll">
            <Combobox.Option value="__none" onClick={() => select(undefined)}>
              <Text size="sm" c="dimmed">
                {t('picker.noProject')}
              </Text>
            </Combobox.Option>
            {groups.favorites.length > 0 && (
              <Combobox.Group
                label={
                  <Group gap={4}>
                    <IconStarFilled size={12} /> {t('picker.favorites')}
                  </Group>
                }
              >
                {groups.favorites.map((p) => renderProject(p, 'fav:'))}
              </Combobox.Group>
            )}
            {groups.byClient.map(([client, list]) => (
              <Combobox.Group key={client || '__noclient'} label={client || t('picker.withoutClient')}>
                {list.map((p) => renderProject(p, ''))}
              </Combobox.Group>
            ))}
            {projects.length === 0 && <Combobox.Empty>{t('picker.empty')}</Combobox.Empty>}
          </ScrollArea.Autosize>
        </Combobox.Options>
        {onCreateProject && (
          <Combobox.Footer>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconCirclePlus size={14} />}
              onClick={() => {
                onCreateProject(search);
                combobox.closeDropdown();
              }}
            >
              {search ? t('picker.createNamed', { name: search }) : t('picker.create')}
            </Button>
          </Combobox.Footer>
        )}
      </Combobox.Dropdown>
    </Combobox>
  );
}

